import PropTypes from 'prop-types';
import { DeleteIcon, IosArrowRight, IosArrowLeft, SuccessCircle } from '../../assets/icons';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';
import { getAllUsers, createAssignment } from '../../services/vehicleService';
import { useEffect, useState, useMemo } from 'react';
import { formatDate } from '../../helpers/stringHelpers';
import { MultiSelect } from 'primereact/multiselect';
import { getDistricts, getNeighborhoods } from '../../services/addressService';

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

const INITIAL_FORM_STATE = {
  numberPlate: '',
  district: '',
  districtId: '',
  neighbourHood: '',
  assignmentDate: new Date().toISOString().split('T')[0],
  users: []
};

const CreateAssignmentModal = ({ isOpen, onClose, onSuccess }) => {
  // Use useMemo to maintain the initial state across re-renders
  const initialState = useMemo(() => ({
    currentStep: STEPS.FORM,
    formData: INITIAL_FORM_STATE,
    error: null,
    isLoading: false,
    createdAssignment: null,
    districts: [],
    neighborhoods: [],
    storedLocations: [],
    users: [],
    isLoadingDistricts: false,
    isLoadingNeighborhoods: false,
    isLoadingUsers: false,
    selectedUsers: []
  }), []);

  const [state, setState] = useState(initialState);

  // Helper function to update state
  const updateState = (updates) => {
    setState(current => ({
      ...current,
      ...updates
    }));
  };

  useEffect(() => {
    if (isOpen) {
      fetchDistricts();
      fetchUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (state.formData.districtId) {
      fetchNeighborhoods(state.formData.districtId);
    } else {
      updateState({ neighborhoods: [] });
    }
  }, [state.formData.districtId]);

  // Reset state ONLY when modal is explicitly closed
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        setState(initialState);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, initialState]);

  // Add modal closing behaviors
  useModalClose(isOpen, onClose);

  const fetchDistricts = async () => {
    try {
      updateState({ isLoadingDistricts: true });
      const response = await getDistricts();
      const data = await response;
      if (data.status === 200) {
        updateState({ districts: data.data || [] });
      } else {
        updateState({ 
          districts: [],
          error: 'İlçeler yüklenirken bir hata oluştu.'
        });
      }
    } catch (error) {
      console.error('Failed to fetch districts:', error);
      updateState({ 
        districts: [],
        error: 'İlçeler yüklenirken bir hata oluştu.'
      });
    } finally {
      updateState({ isLoadingDistricts: false });
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      updateState({ isLoadingNeighborhoods: true });
      const response = await getNeighborhoods(districtId);
      const data = await response;
      if (data.status === 200) {
        updateState({ neighborhoods: data.data || [] });
      } else {
        updateState({ 
          neighborhoods: [],
          error: 'Mahalleler yüklenirken bir hata oluştu.'
        });
      }
    } catch (error) {
      console.error('Failed to fetch neighborhoods:', error);
      updateState({ 
        neighborhoods: [],
        error: 'Mahalleler yüklenirken bir hata oluştu.'
      });
    } finally {
      updateState({ isLoadingNeighborhoods: false });
    }
  };

  const fetchUsers = async () => {
    try {
      updateState({ isLoadingUsers: true });
      const response = await getAllUsers();
      if (response.status === 200) {
        updateState({ users: response.users || [] });
      } else {
        updateState({ 
          users: [],
          error: 'Kullanıcılar yüklenirken bir hata oluştu.'
        });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      updateState({ 
        users: [],
        error: 'Kullanıcılar yüklenirken bir hata oluştu.'
      });
    } finally {
      updateState({ isLoadingUsers: false });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'districtId') {
      const selectedDistrict = state.districts.find(d => d.id === value);
      updateState({
        formData: {
          ...state.formData,
          district: selectedDistrict ? selectedDistrict.ad : '',
          districtId: value,
          neighbourHood: '' // Reset neighborhood when district changes
        }
      });
    } else {
      updateState({
        formData: {
          ...state.formData,
          [name]: value
        }
      });
    }
  };

  const handleAddLocation = () => {
    if (!state.formData.districtId || !state.formData.neighbourHood) {
      updateState({ error: 'Lütfen ilçe ve mahalle seçiniz' });
      return;
    }

    const selectedDistrict = state.districts.find(d => d.id === state.formData.districtId);
    const selectedNeighborhood = state.neighborhoods.find(n => n.ad === state.formData.neighbourHood);

    if (!selectedDistrict || !selectedNeighborhood) {
      updateState({ error: 'Geçersiz ilçe veya mahalle seçimi' });
      return;
    }

    const newLocation = {
      districtId: selectedDistrict.id,
      districtName: selectedDistrict.ad,
      neighborhoodId: selectedNeighborhood.id,
      neighborhoodName: selectedNeighborhood.ad
    };

    updateState({
      storedLocations: [...state.storedLocations, newLocation],
      error: '',
      formData: {
        ...state.formData,
        districtId: '',
        neighbourHood: ''
      }
    });
  };

  const handleDeleteLocation = (index) => {
    updateState({
      storedLocations: state.storedLocations.filter((_, i) => i !== index)
    });
  };

  const validateForm = () => {
    updateState({ error: '' });
    
    // Validate date is selected
    if (!state.formData.assignmentDate) {
      updateState({ error: 'Tarih seçilmelidir' });
      return false;
    }
    
    // Validate date is not in the past
    const selectedDate = new Date(state.formData.assignmentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!selectedDate) {
      updateState({ error: 'Tarih seçilmelidir' });
      return false;
    }
    
    if (selectedDate < today) {
      updateState({ error: 'Geçmiş bir tarih seçilemez' });
      return false;
    }

    // Validate plate number
    if (!state.formData.numberPlate.trim()) {
      updateState({ error: 'Plaka numarası girilmelidir' });
      return false;
    }

    // Validate location
    if (state.storedLocations.length !== 1) {
      updateState({ error: 'Bir görevlendirme yeri seçilmelidir' });
      return false;
    }

    return true;
  };

  const handleUserSelect = (userId) => {
    updateState({
      selectedUsers: state.selectedUsers.includes(userId)
        ? state.selectedUsers.filter(id => id !== userId)
        : [...state.selectedUsers, userId]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Use the stored location for submission
    const location = state.storedLocations[0];
    updateState({
      formData: {
        ...state.formData,
        districtId: location.districtId,
        district: location.districtName,
        neighbourHood: location.neighborhoodName
      },
      currentStep: STEPS.CONFIRM
    });
  };

  const handleNewAssignment = () => {
    setState(initialState);
  };

  const handleUserSubmit = async () => {
    if (state.selectedUsers.length === 0) {
      updateState({ error: 'En az bir kişi seçilmelidir' });
      return;
    }

    updateState({ isLoading: true, error: null });

    try {
      const result = await createAssignment({
        ...state.formData,
        users: state.selectedUsers
      });
      
      const assignmentData = {
        ...state.formData,
        users: state.selectedUsers,
        assigmentId: result.id // Assuming the API returns an ID
      };
      
      updateState({
        createdAssignment: assignmentData,
        currentStep: STEPS.COMPLETE
      });
      
      // Notify parent component with a slight delay to ensure our state updates first
      setTimeout(() => {
        onSuccess(assignmentData);
      }, 100);
    } catch (error) {
      console.error('Failed to create assignment:', error);
      updateState({ 
        error: error.message || 'Görev oluşturulurken bir hata oluştu.',
        currentStep: STEPS.CONFIRM,
        isLoading: false
      });
    } finally {
      updateState({ isLoading: false });
    }
  };

  const handleBack = () => {
    if (state.currentStep === STEPS.CONFIRM) {
      updateState({ currentStep: STEPS.FORM });
    } else if (state.currentStep === STEPS.COMPLETE) {
      updateState({ currentStep: STEPS.CONFIRM });
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${state.currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {state.currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Görevlendirme ve Araç<br/>Bilgileri</span>
      </div>
      <div className={`step ${state.currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {state.currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Atanan Kişi<br/>Bilgileri</span>
      </div>
      <div className={`step ${state.currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {state.currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderStoredLocations = () => (
    <div className="stored-locations">
      <h3 className='form-title'>Eklenen Görevlendirme Yeri</h3>
      {state.storedLocations.map((location, index) => (
        <div key={index} className="location-row">
          <span>{location.districtName} - {location.neighborhoodName} Mahallesi</span>
          <button 
            type="button" 
            className="delete-location-button"
            onClick={() => handleDeleteLocation(index)}
          >
            <DeleteIcon color='#2A353D' />
          </button>
        </div>
      ))}
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="create-assignment-form">
      <div className="form-group">
        <div className="date-input-container">
          <input
            type="date"
            id="assignmentDate"
            name="assignmentDate"
            value={state.formData.assignmentDate}
            onChange={handleInputChange}
          />
        </div>
        <div className="input-container">
          <input
            type="text"
            id="numberPlate"
            name="numberPlate"
            value={state.formData.numberPlate}
            maxLength={10}
            onChange={handleInputChange}
          />
          {!state.formData.numberPlate && (
            <div className="placeholder">
              Plaka Giriniz <span className="required">(zorunlu)</span>
            </div>
          )}
        </div>
      </div>
      <h3 className='form-title'>Yeni Görevlendirme Yeri Bilgileri</h3>
      <div className="form-group">
        <div className="select-container">
          <select
            name="districtId"
            value={state.formData.districtId}
            onChange={handleInputChange}
            className={state.formData.districtId ? 'has-value' : ''}
          >
            <option value=""></option>
            {state.districts.map(district => (
              <option key={district.id} value={district.id}>
                {district.ad}
              </option>
            ))}
          </select>
          {!state.formData.districtId && (
            <div className="placeholder">
              İlçe seçiniz <span className="required">(zorunlu)</span>
            </div>
          )}
        </div>
      </div>
      <div className="form-group">
        <div className="select-container">
          <select
            name="neighbourHood"
            value={state.formData.neighbourHood}
            onChange={handleInputChange}
            disabled={!state.formData.districtId}
            className={state.formData.neighbourHood ? 'has-value' : ''}
          >
            <option value=""></option>
            {state.neighborhoods.map(neighborhood => (
              <option key={neighborhood.id} value={neighborhood.ad}>
                {neighborhood.ad}
              </option>
            ))}
          </select>
          {!state.formData.neighbourHood && (
            <div className="placeholder">
              Mahalle seçiniz <span className="required">(zorunlu)</span>
            </div>
          )}
        </div>
      </div>
      <button 
        type="button" 
        className="add-location-button"
        onClick={handleAddLocation}
      >
        Görevlendirme Yeri Ekle
      </button>
      {renderStoredLocations()}
      {state.error && <div className="error-message">{state.error}</div>}
      <div className="form-actions">
        <button type="submit" className="submit-button">
          <p>Sonraki Adıma Geç</p>
          <IosArrowRight color='#18964E' />
        </button>
      </div>
    </form>
  );

  const renderConfirmation = () => (
    <div className="create-assignment-modal-confirmation-step">
      <div className='previous-info-container'>
        <h3 className='form-title'>Önceki Adımdaki Görevlendirme Bilgileri</h3>
        <p className='plate-and-date'>{state.formData.numberPlate} ({formatDate(state.formData.assignmentDate)})</p>
        <p className='district-and-neighborhood'>{state.formData.district} <span>{state.formData.neighbourHood} Mahallesi</span></p>
      </div>
      <div className="user-selection">
        <h3 className="form-title">Kişi Seçimi</h3>
        <MultiSelect 
          value={state.selectedUsers} 
          onChange={(e) => updateState({ selectedUsers: e.value })} 
          options={state.users.map(user => ({
            label: `${user.ad}`,
            value: user.id
          }))}
          optionLabel="label"
          placeholder={state.selectedUsers.length > 0 
            ? `${state.selectedUsers.length} kişi seçildi`
            : <div>Kişi seçiniz. <span className="required">(Çoktan seçmeli, zorunlu)</span></div>
          }
          className='multi-select-container'
          filter
          filterPlaceholder='Arama Yap'
          hideOnClickOutside={true}
        />

        {state.selectedUsers.length > 0 && (
          <div className="selected-users">
            <h3 className='form-title'>Eklenen Kişiler:</h3>
            <div className='selected-users-container'>
              {state.selectedUsers.map(userId => {
                const user = state.users.find(u => u.id == userId);
                if (!user) return null;
                return (
                  <div key={userId} className="selected-user">
                    <p>{user.ad}</p>
                    <p>{user.tc}</p>
                    <button
                      type="button"
                      onClick={() => handleUserSelect(userId)}
                    >
                      <DeleteIcon color="#2A353D" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {state.error && <div className="error-message">{state.error}</div>}
      <div className="form-actions confirmation-form-actions">
        <button type="button" onClick={handleBack} className="back-button">
          <IosArrowLeft color='#2A353D' /> Önceki Adıma Dön
        </button>
        <button type="button" onClick={handleUserSubmit} className="confirm-button">
          Sonraki Adıma Geç <IosArrowRight color='#18964E'/>
        </button>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="complete-step assignment-complete-step">
      {state.isLoading ? (
        <div className="loading-state">
          <Spinner size="large" />
          <h3>Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="cancel-button">
            Süreci İptal Et
          </button>
        </div>
      ) : state.createdAssignment ? (
        <div className="success-state">
          <SuccessCircle />
          <h3>Süreciniz tamamlandı. Aşağıdaki görev sisteme eklendi.</h3>
          <div className='assignment-info-container'>
            <p>Plaka: {state.createdAssignment.numberPlate} adlı aracın <span>{state.createdAssignment.district} {state.createdAssignment.neighbourHood}</span> görevlendirmesi oluşturulmuştur.</p>
            <p>İlçe: {state.createdAssignment.district}</p>
            <p>Bu sürecin takibini {state.createdAssignment.users.map(userId => {
              const user = state.users.find(u => u.id === userId);
              return user ? <span className="assigned-user-name" key={userId}>{user.ad}</span> : '';
            }).reduce((prev, curr) => [prev, ', ', curr])} gerçekleştirecektir.</p>
          </div>
          <div className="form-actions">
            <button type="button" onClick={handleNewAssignment} className="new-assignment-button">
              Yeni Görevlendirme Oluştur
            </button>
          </div>
        </div>
      ) : (
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h3>Hata Oluştu</h3>
          <p>{state.error}</p>
          <button type="button" onClick={handleNewAssignment} className="retry-button">
            Yeni Görevlendirme Oluştur
          </button>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Yeni Görevlendirme Ekle</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {state.currentStep === STEPS.FORM && renderForm()}
          {state.currentStep === STEPS.CONFIRM && renderConfirmation()}
          {state.currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

CreateAssignmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default CreateAssignmentModal; 